import React, { useRef, useEffect, useCallback } from 'react';

interface TextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  autoResize?: boolean;
}

export const Textarea: React.FC<TextareaProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  className = '',
  id,
  rows = 3,
  minRows,
  maxRows = 10,
  autoResize = true
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const effectiveMinRows = minRows ?? rows;

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !autoResize) return;

    // Reset height to calculate scrollHeight correctly
    textarea.style.height = 'auto';

    // Calculate line height from computed styles
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight) || 24;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
    const borderTop = parseInt(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseInt(computedStyle.borderBottomWidth) || 0;

    const minHeight = (effectiveMinRows * lineHeight) + paddingTop + paddingBottom + borderTop + borderBottom;
    const maxHeight = (maxRows * lineHeight) + paddingTop + paddingBottom + borderTop + borderBottom;

    // Set new height based on content, clamped between min and max
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;

    // Enable scrolling only when content exceeds maxHeight
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [autoResize, effectiveMinRows, maxRows]);

  // Adjust height on value change
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Adjust height on mount and window resize
  useEffect(() => {
    adjustHeight();
    window.addEventListener('resize', adjustHeight);
    return () => window.removeEventListener('resize', adjustHeight);
  }, [adjustHeight]);

  return (
    <textarea
      ref={textareaRef}
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={effectiveMinRows}
      className={`
        w-full px-3 py-2
        bg-white/5 border border-white/30 rounded-lg
        text-foreground-primary placeholder:text-muted-foreground
        focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 focus:outline-none
        hover:border-white/40
        disabled:opacity-50 disabled:cursor-not-allowed
        resize-none
        transition-colors duration-200
        backdrop-blur-sm
        ${className}
      `}
    />
  );
};